import React, { useContext, useState, useEffect } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useSnackbar } from "../../contexts/InfoContext";
import { useAuth } from "../../contexts/AuthContext";
import getInternationalDate from "../../utils/getDate";
import CustomTextField from "../Credit/component/CustomTextField";
import { SpinnerContext } from "../../contexts/SpinnerContext";
import getRequiredUserData from "../../utils/getBranchInfo";
import editGivenAccessoriesToDeliveryGuys from "../../api/accessories_given_to_workers/edit_given_accessories_to_deliveryGuy";
import { getAccessoryTypes } from "../../api/accessories_given_to_workers/accessoryTypes";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  accessoriesGiven: Yup.array().of(
    Yup.object().shape({
      accessoryType: Yup.string().required("Accessory Type is required"),
      quantity: Yup.number()
        .required("Quantity is required")
        .positive("Quantity must be positive")
        .integer("Quantity must be an integer")
        .min(1, "Quantity must be at least 1"),
    })
  ),
  notes: Yup.string().nullable(),
});

const GivenAccessoriesToDeliveryGuyEditForm = ({
  givenAccessory,
  isEditDialogOpen,
  closeEditDialog,
}) => {
  const { openSnackbar } = useSnackbar();
  const { user } = useAuth();
  const theme = useTheme();
  const userData = getRequiredUserData();
  const { setIsSubmitting } = useContext(SpinnerContext);
  const [accessories, setAccessories] = useState([]);

  useEffect(() => {
    if (userData.requiredId) {
      const unsubscribe = getAccessoryTypes(
        userData.requiredId,
        setAccessories
      );
      return () => unsubscribe && unsubscribe();
    }
  }, [userData.requiredId]);

  const formik = useFormik({
    initialValues: {
      deliveryGuyName: givenAccessory.deliveryGuyName || "",
      accessoriesGiven: givenAccessory.accessoriesGiven || [],
      notes: givenAccessory.notes || "",
      returned: givenAccessory.returned || false,
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const date = getInternationalDate();

        const payload = {
          ...givenAccessory,
          ...values,
          date: date,
          accessoriesGiven: values.accessoriesGiven.map((item) => ({
            ...item,
            quantity: parseInt(item.quantity, 10),
          })),
        };

        const res = await editGivenAccessoriesToDeliveryGuys(
          user,
          payload,
          givenAccessory.id
        );
        openSnackbar(`${res.data.message}!`, "success");
        handleCloseForm();
      } catch (error) {
        if (error.response && error.response.data) {
          openSnackbar(
            error.response.data.message,
            error.response.data.type ? error.response.data.type : "error"
          );
        } else {
          openSnackbar(
            "An unexpected error occurred. Please kindly check your connection.",
            "error"
          );
        }
      }
      setIsSubmitting(false);
    },
  });

  const handleCloseForm = () => {
    closeEditDialog();
    formik.resetForm();
  };

  const handleAddAccessoryItem = () => {
    formik.setFieldValue("accessoriesGiven", [
      ...formik.values.accessoriesGiven,
      { accessoryType: "", quantity: 1 },
    ]);
  };

  const handleRemoveAccessoryItem = (index) => {
    const newAccessories = [...formik.values.accessoriesGiven];
    newAccessories.splice(index, 1);
    formik.setFieldValue("accessoriesGiven", newAccessories);
  };

  const handleAccessoryItemChange = (index, field, value) => {
    const newAccessories = [...formik.values.accessoriesGiven];
    newAccessories[index] = { ...newAccessories[index], [field]: value };
    formik.setFieldValue("accessoriesGiven", newAccessories);
  };
  useEffect(() => {
    const currentAccessoriesLength = formik.values.accessoriesGiven.length;
    const isCurrentlyReturned = formik.values.returned;

    if (currentAccessoriesLength === 0 && !isCurrentlyReturned) {
      formik.setFieldValue("returned", true);
    } else if (currentAccessoriesLength > 0 && isCurrentlyReturned) {
      formik.setFieldValue("returned", false);
    }
  }, [
    formik.values.accessoriesGiven.length,
    formik.values.returned,
    formik.setFieldValue,
  ]);

  return (
    <Dialog
      open={isEditDialogOpen}
      onClose={handleCloseForm}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ backgroundColor: theme.palette.background.alt }}>
        Edit Given Accessory To Delivery Guy
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: theme.palette.background.alt }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="deliveryGuyName"
                label="Delivery Guy Name"
                value={formik.values.deliveryGuyName}
                fullWidth
                disabled
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                name="notes"
                label="Notes"
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                errors={formik.errors}
                touched={formik.touched}
                multiline
                minRows={3}
                maxRows={5}
              />
            </Grid>

            {formik.values.accessoriesGiven.map((item, index) => (
              <Grid item xs={12} key={index}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TextField
                      select
                      name={`accessoriesGiven[${index}].accessoryType`}
                      label={`Accessory Type ${index + 1}`}
                      value={item.accessoryType}
                      onChange={(e) =>
                        handleAccessoryItemChange(
                          index,
                          "accessoryType",
                          e.target.value
                        )
                      }
                      onBlur={formik.handleBlur}
                      fullWidth
                      error={
                        formik.touched.accessoriesGiven?.[index]
                          ?.accessoryType &&
                        Boolean(
                          formik.errors.accessoriesGiven?.[index]?.accessoryType
                        )
                      }
                      helperText={
                        formik.touched.accessoriesGiven?.[index]
                          ?.accessoryType &&
                        formik.errors.accessoriesGiven?.[index]?.accessoryType
                      }
                      InputLabelProps={{ shrink: true }}
                    >
                      <MenuItem value="">Select Accessory</MenuItem>
                      {accessories.map((accessory) => (
                        <MenuItem key={accessory.id} value={accessory.type}>
                          {accessory.type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      name={`accessoriesGiven[${index}].quantity`}
                      label="Quantity"
                      type="number"
                      value={item.quantity}
                      onChange={(e) =>
                        handleAccessoryItemChange(
                          index,
                          "quantity",
                          e.target.value
                        )
                      }
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.accessoriesGiven?.[index]?.quantity &&
                        Boolean(
                          formik.errors.accessoriesGiven?.[index]?.quantity
                        )
                      }
                      helperText={
                        formik.touched.accessoriesGiven?.[index]?.quantity &&
                        formik.errors.accessoriesGiven?.[index]?.quantity
                      }
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      color="error"
                      fullWidth
                      onClick={() => handleRemoveAccessoryItem(index)}
                    >
                      Return
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Button variant="contained" onClick={handleAddAccessoryItem}>
                Add Accessory
              </Button>
              {typeof formik.errors.accessoriesGiven === "string" && (
                <Typography
                  variant="caption"
                  color="error"
                  style={{ marginTop: "4px", display: "block" }}
                >
                  {formik.errors.accessoriesGiven}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} container alignItems="end" justifyContent="end">
              <DialogActions
                sx={{ backgroundColor: theme.palette.background.alt }}
              >
                <Button
                  variant="contained"
                  onClick={handleCloseForm}
                  color="secondary"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={formik.isSubmitting}
                >
                  Update
                </Button>
              </DialogActions>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GivenAccessoriesToDeliveryGuyEditForm;
